import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import mcpApi, { setMcpApiBase } from './utils/api';
import { Field, TextInput, Select } from '../cognitive-flows/components/shared/FormFields';
import { dashboardStyles as d } from '../cognitive-flows/dashboardStyles';
import '../cognitive-flows/styles/global.css';

const STATUS_META = {
    connected: { label: 'Connected', color: 'var(--success)', bg: 'var(--success-light)' },
    error: { label: 'Error', color: 'var(--error)', bg: 'var(--error-light)' },
    needs_auth: { label: 'Needs auth', color: 'var(--warning)', bg: 'var(--warning-light)' },
    unconfigured: { label: 'Not tested', color: 'var(--text-secondary)', bg: '#f1f5f9' },
};

function StatusChip({ status }) {
    const m = STATUS_META[status] || STATUS_META.unconfigured;
    return (
        <span style={{ fontSize: '11px', fontWeight: 600, color: m.color, background: m.bg, padding: '3px 9px', borderRadius: '99px' }}>
            {m.label}
        </span>
    );
}

const s = {
    wrap: { padding: '24px', background: 'var(--bg)', minHeight: '100%' },
    card: {
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
        padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '12px', minHeight: '150px',
        boxShadow: 'var(--shadow-sm)',
    },
    cardTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' },
    name: { fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' },
    kindBadge: { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' },
    url: { fontSize: '12px', color: 'var(--text-secondary)', wordBreak: 'break-all', lineHeight: 1.4 },
    cardActions: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: 'auto' },
    btnSm: { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: 'var(--radius)', cursor: 'pointer' },
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px',
    },
    modal: {
        background: 'var(--surface)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '520px',
        maxHeight: '88vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)', padding: '24px',
    },
    modalTitle: { fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' },
    tabs: { display: 'flex', gap: '4px', background: '#f1f5f9', borderRadius: 'var(--radius)', padding: '4px', margin: '16px 0 20px' },
    tab: (active) => ({
        flex: 1, padding: '8px', fontSize: '13px', fontWeight: 600, borderRadius: 'var(--radius)',
        background: active ? 'var(--surface)' : 'transparent', color: active ? 'var(--primary)' : 'var(--text-secondary)',
        boxShadow: active ? 'var(--shadow-sm)' : 'none', cursor: 'pointer',
    }),
    catItem: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
        border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 16px', marginBottom: '10px',
    },
};

function AddModal({ catalog, onClose, onCreated, addCatalog }) {
    const [tab, setTab] = useState('custom');
    const [form, setForm] = useState({ name: '', url: '', transport: 'streamable-http', authType: 'none', token: '' });
    const [saving, setSaving] = useState(false);
    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    const submitCustom = async () => {
        if (!form.name.trim() || !form.url.trim()) {
            toast.error('Name and URL are required');
            return;
        }
        setSaving(true);
        try {
            const body = {
                kind: 'custom',
                name: form.name.trim(),
                url: form.url.trim(),
                transport: form.transport,
                auth: { type: form.authType, ...(form.authType === 'bearer' ? { token: form.token } : {}) },
            };
            const { data } = await mcpApi.post('/connectors', body);
            toast.success('Connector added — click Test to connect');
            onCreated(data.connector);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to add connector');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={s.overlay} onClick={onClose}>
            <div style={s.modal} onClick={(e) => e.stopPropagation()}>
                <div style={s.modalTitle}>Add connector</div>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Connect a remote MCP server to query its data.</p>

                <div style={s.tabs}>
                    <div style={s.tab(tab === 'custom')} onClick={() => setTab('custom')}>Custom server</div>
                    <div style={s.tab(tab === 'catalog')} onClick={() => setTab('catalog')}>Catalog</div>
                </div>

                {tab === 'custom' ? (
                    <div>
                        <Field label="Name"><TextInput value={form.name} onChange={set('name')} placeholder="My MCP server" /></Field>
                        <Field label="Server URL" hint="Must be an https:// Streamable-HTTP MCP endpoint.">
                            <TextInput value={form.url} onChange={set('url')} placeholder="https://mcp.example.com/mcp" />
                        </Field>
                        <Field label="Transport">
                            <Select
                                value={form.transport}
                                onChange={set('transport')}
                                options={[{ value: 'streamable-http', label: 'Streamable HTTP' }, { value: 'sse', label: 'SSE (legacy)' }]}
                            />
                        </Field>
                        <Field label="Authentication">
                            <Select
                                value={form.authType}
                                onChange={set('authType')}
                                options={[
                                    { value: 'none', label: 'None' },
                                    { value: 'bearer', label: 'Bearer token / API key' },
                                    { value: 'oauth2', label: 'OAuth 2.1' },
                                ]}
                            />
                        </Field>
                        {form.authType === 'bearer' && (
                            <Field label="Token" hint="Stored encrypted; never shown again.">
                                <TextInput value={form.token} onChange={set('token')} placeholder="sk-… or a bearer token" />
                            </Field>
                        )}
                        {form.authType === 'oauth2' && (
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>You'll authorize in a popup after adding the connector.</p>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                            <button style={d.btnGhost} onClick={onClose}>Cancel</button>
                            <button style={{ ...d.btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={submitCustom}>
                                {saving ? 'Adding…' : 'Add connector'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        {catalog.map((c) => (
                            <div key={c.id} style={s.catItem}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>{c.name}</div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{c.description}</div>
                                </div>
                                <button
                                    style={{ ...d.btnPrimary, ...s.btnSm, opacity: c.configured ? 1 : 0.5 }}
                                    disabled={!c.configured}
                                    title={c.configured ? 'Connect via OAuth' : 'Not configured in this environment'}
                                    onClick={() => addCatalog(c)}
                                >
                                    Connect
                                </button>
                            </div>
                        ))}
                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
                            Catalog connectors authenticate via OAuth in a popup. Each server's URL must be configured in the environment to enable it.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

function ToolsModal({ connector, tools, onClose }) {
    return (
        <div style={s.overlay} onClick={onClose}>
            <div style={s.modal} onClick={(e) => e.stopPropagation()}>
                <div style={s.modalTitle}>{connector.name} — tools</div>
                {tools.length === 0 ? (
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '12px' }}>
                        No tools cached yet. Run “Test” to connect and list this server’s tools.
                    </p>
                ) : (
                    <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {tools.map((t) => (
                            <div key={t.name} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
                                <div style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 700, color: 'var(--accent)' }}>{t.name}</div>
                                {t.description && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{t.description}</div>}
                            </div>
                        ))}
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button style={d.btnGhost} onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}

export default function MCPConnectorsPage({ app_url }) {
    const [connectors, setConnectors] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [toolsModal, setToolsModal] = useState(null); // { connector, tools }

    useEffect(() => {
        setMcpApiBase(app_url);
    }, [app_url]);

    const load = useCallback(async () => {
        try {
            const [conn, cat] = await Promise.all([mcpApi.get('/connectors'), mcpApi.get('/catalog')]);
            setConnectors(conn.data.connectors || []);
            setCatalog(cat.data.catalog || []);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to load connectors');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const testConnector = async (c) => {
        setBusyId(c._id);
        try {
            const { data } = await mcpApi.post(`/connectors/${c._id}/test`);
            toast.success(`Connected — ${data.tools?.length ?? 0} tool(s)`);
            setConnectors((list) => list.map((x) => (x._id === c._id ? data.connector : x)));
        } catch (err) {
            toast.error(err.response?.data?.error || 'Connection failed');
            await load();
        } finally {
            setBusyId(null);
        }
    };

    const removeConnector = async (c) => {
        if (!window.confirm(`Delete connector “${c.name}”?`)) return;
        setBusyId(c._id);
        try {
            await mcpApi.delete(`/connectors/${c._id}`);
            setConnectors((list) => list.filter((x) => x._id !== c._id));
            toast.success('Connector deleted');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Delete failed');
        } finally {
            setBusyId(null);
        }
    };

    const viewTools = async (c) => {
        try {
            const { data } = await mcpApi.get(`/connectors/${c._id}/tools`);
            setToolsModal({ connector: c, tools: data.tools || [] });
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to load tools');
        }
    };

    // Open the OAuth authorize URL in a popup, then refresh when it closes.
    const startOAuthPopup = (connectorId) =>
        new Promise((resolve) => {
            const popup = window.open('about:blank', 'mcp_oauth', 'width=600,height=720');
            mcpApi
                .post(`/connectors/${connectorId}/oauth/start`)
                .then(({ data }) => {
                    if (data.authorized) {
                        popup?.close();
                        toast.success('Connected');
                        return resolve(true);
                    }
                    if (!data.authorizeUrl || !popup) {
                        popup?.close();
                        toast.error('Could not start OAuth');
                        return resolve(false);
                    }
                    popup.location.href = data.authorizeUrl;
                    const timer = setInterval(() => {
                        if (popup.closed) {
                            clearInterval(timer);
                            resolve(true);
                        }
                    }, 600);
                })
                .catch((err) => {
                    popup?.close();
                    toast.error(err.response?.data?.error || 'OAuth failed to start');
                    resolve(false);
                });
        });

    const addCatalog = async (entry) => {
        try {
            const { data } = await mcpApi.post('/connectors', { kind: 'catalog', catalogId: entry.id });
            setAddOpen(false);
            // OAuth tiles authorize in a popup; no-auth tiles (e.g. the self-hosted
            // BigQuery toolbox) just connect — test to cache tools + mark connected.
            if (entry.auth?.type === 'oauth2') {
                await startOAuthPopup(data.connector._id);
            } else {
                await mcpApi.post(`/connectors/${data.connector._id}/test`).catch(() => {});
            }
            await load();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to add catalog connector');
        }
    };

    const reconnectOAuth = async (c) => {
        await startOAuthPopup(c._id);
        await load();
    };

    return (
        <div style={s.wrap}>
            <div style={d.sectionHead}>
                <div>
                    <h1 style={d.sectionTitle}>Connectors</h1>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                        Connect MCP servers and query their data.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <a href="/cognitive-flows/chat" style={{ ...d.btnGhost, textDecoration: 'none' }}>Query →</a>
                    <button style={d.btnPrimary} onClick={() => setAddOpen(true)}>+ Add connector</button>
                </div>
            </div>

            {loading ? (
                <div style={d.empty}>Loading…</div>
            ) : connectors.length === 0 ? (
                <div style={d.empty}>
                    <p style={{ fontSize: '15px', marginBottom: '6px' }}>No connectors yet</p>
                    <p style={{ fontSize: '13px' }}>Add a remote MCP server to get started.</p>
                </div>
            ) : (
                <div style={d.grid}>
                    {connectors.map((c) => {
                        const toolCount = c.toolCache?.tools?.length;
                        const busy = busyId === c._id;
                        return (
                            <div key={c._id} style={s.card}>
                                <div style={s.cardTop}>
                                    <div>
                                        <div style={s.name}>{c.name}</div>
                                        <div style={s.kindBadge}>{c.kind === 'catalog' ? `Catalog · ${c.catalogId}` : 'Custom'}</div>
                                    </div>
                                    <StatusChip status={c.status} />
                                </div>
                                <div style={s.url}>{c.url}</div>
                                {c.lastError && <div style={{ fontSize: '11px', color: 'var(--error)' }}>{c.lastError}</div>}
                                {typeof toolCount === 'number' && (
                                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{toolCount} tool(s) cached</div>
                                )}
                                <div style={s.cardActions}>
                                    {c.auth?.type === 'oauth2' && c.status !== 'connected' && (
                                        <button style={{ ...d.btnPrimary, ...s.btnSm }} disabled={busy} onClick={() => reconnectOAuth(c)}>
                                            Connect
                                        </button>
                                    )}
                                    <button
                                        style={{ ...d.btnPrimary, ...s.btnSm, opacity: busy ? 0.6 : 1 }}
                                        disabled={busy}
                                        onClick={() => testConnector(c)}
                                    >
                                        {busy ? 'Testing…' : 'Test'}
                                    </button>
                                    <button style={{ ...d.btnGhost, ...s.btnSm }} onClick={() => viewTools(c)}>Tools</button>
                                    <button
                                        style={{ ...d.btnGhost, ...s.btnSm, color: 'var(--error)', borderColor: 'var(--error-light)' }}
                                        disabled={busy}
                                        onClick={() => removeConnector(c)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {addOpen && (
                <AddModal
                    catalog={catalog}
                    onClose={() => setAddOpen(false)}
                    onCreated={async (connector) => {
                        setConnectors((list) => [connector, ...list]);
                        setAddOpen(false);
                        if (connector.auth?.type === 'oauth2') {
                            await startOAuthPopup(connector._id);
                            await load();
                        }
                    }}
                    addCatalog={addCatalog}
                />
            )}
            {toolsModal && (
                <ToolsModal connector={toolsModal.connector} tools={toolsModal.tools} onClose={() => setToolsModal(null)} />
            )}
        </div>
    );
}
